import { Container, Hidden, InputLabel } from '@material-ui/core';
import { ApiError } from 'api/api-error';
import catalogueApi, { SaleType } from 'api/query/catalogue';
import Button from 'components/button';
import CascadingMenu, { CascadingMenuItem } from 'components/cascading-menu';
import { FooterBar } from 'components/footer-bar/footer-bar';
import BackToCatalogue from 'pages/catalogues/back-to-catalogue';
import { CatalogueOperation } from 'pages/catalogues/catalogue-operation.enum';
import { databaseTypes, getDefaultDatabase } from 'pages/search/prime/prime-utils';
import { usePrime } from 'pages/search/prime/use-prime';
import React, { useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { useNavigation } from 'react-navi';
import { toQuerystring } from 'utils/navigation-utils';
import { addToStorage, StorageKey } from 'utils/storage-utils';
import { useEventListener } from 'utils/use-event-listener';
import AddAnimals from './add-animals';
import AddDetails from './add-details';
import { CatalogueFormField } from './catalogue-form-field.enum';
import { catalogueFormValidation } from './catalogue-form-validation';
import { CreateCatalogueFormProps } from './create-catalogue-form-props';
import {
  getBackToCataloguesQuerystring, getCatalogueInitialState, getCataloguePayload, getHeader, getStepLabel,
} from './create-catalogue-form-utils';
import './create-catalogue-form.scss';
import CreateSaleCatalogue from './create-sale-catalogue';
import CreateSemenCatalogue from './create-semen-catalogue';

/**
 * Create/edit sale or semen catalogue form.
 *
 * This component stores the current step and renders the appropriate component.
 *
 * Note: This does not render on `phone` breakpoint.
 * @param props
 */
export const CreateCatalogueForm: React.FC<CreateCatalogueFormProps> = (props) => {
  const {
    type,
    db,
    userId,
    catalogue,
    operation = CatalogueOperation.Create,
    step = 1,
  } = props;

  const {
    primeFilters, updatePrime,
  } = usePrime();
  const {
    userType, db: primeDb,
  } = primeFilters;
  const [defaultDatabase] = useState(getDefaultDatabase(primeDb.id, databaseTypes));

  const navigation = useNavigation();
  const methods = useForm({ mode: 'onBlur', validateCriteriaMode: 'all' });
  const { handleSubmit, setError } = methods;
  const [saving, setSaving] = useState<boolean>(false);
  const [publishing, setPublishing] = useState<boolean>(false);
  const [apiErrors, setApiErrors] = useState<ApiError[]>([]);

  const updateDB = (item: CascadingMenuItem) => {
    // updates database filter state
    const newDb = {
      value: item.value,
      id: item.id,
    };
    const dbFilters = { db: newDb };
    updatePrime(dbFilters);
    // Saving to local storage even if trait or search type wasn't selected.
    // It's needed when 'back to dashboard' from catalogues to populate selected options.
    addToStorage(StorageKey.Prime, { userType, ...dbFilters });
  };

  /**
   * Add beforeunload warning if the user tries to navigate away from this page.
   */
  useEventListener('beforeunload', (event: any) => {
    event.preventDefault();
    // eslint-disable-next-line no-param-reassign
    event.returnValue = '';
  });

  /**
   * Navigates back a step.
   *
   * This uses the `window.location` api to allow the `beforeunload`
   * prompt to display before navigating away.
   */
  const back = () => {
    const { query } = navigation.getCurrentValue().url;
    const q = { ...query };
    q.step = `${step - 1}`;
    window.location.replace(toQuerystring(q));
  };

  /**
   * Creates a new catalogue from valid form data.
   * @param data The form data.
   */
  const create = async (data: Record<string, any>) => {
    const payload = await getCataloguePayload(data, getCatalogueInitialState());
    payload.dbType = primeDb.id;
    const response = await catalogueApi.create(payload, userId);
    if (response.errors) {
      setApiErrors(response.errors);
    } else {
      setApiErrors([]);
      // On success, navigate to the edit mode.
      // Store the catalogue in the history state so that it can be passed
      // to the Edit component as a prop.
      // Create can only ever be done on step 1, so hard code the change to step 2.
      const { query } = navigation.getCurrentValue().url;
      query.type = data[CatalogueFormField.SaleType] || type;
      query.step = '2';
      navigation.navigate(`./edit/${response.catalogue.id}${toQuerystring(query)}`,
        { replace: true, state: { catalogue: response.catalogue, apiErrors: response.errors } });
    }
  };

  /**
   * Edit an existing catalogue from valid form data.
   * @param data The form data.
   * @param isActive The active state of the catalogue (false = draft, true = published)
   */
  const edit = async (data: Record<string, any>, isActive = false) => {
    const payload = await getCataloguePayload(data, catalogue, isActive);
    const response = await catalogueApi.update(payload);
    if (response.errors) {
      setApiErrors(response.errors);
    } else {
      setApiErrors([]);
      // Get the existing querystring params and navigate to the next step.
      const { query } = navigation.getCurrentValue().url;
      if (step < 3) {
        // Only increment the step counter if we're not on the last page.
        query.step = `${step + 1}`;
      }

      // TODO: Update navigation forward to directly set the href
      // Don't use the react-navi navigation object here to navigate to ensure
      // that the window object unloads and triggers the beforeunload event.
      // window.location.href = toQuerystring(query);

      navigation.navigate(toQuerystring(query), {
        state: { catalogue: response.catalogue, apiErrors: response.errors }, replace: true,
      });
    }
  };

  /**
   * Submits the valid form data.
   * @param data
   */
  const submit = async (data: Record<string, any>) => {
    switch (operation) {
      case CatalogueOperation.Create:
        return create(data);
      case CatalogueOperation.Edit:
        return edit(data);
      default:
        throw Error('Unable to submit, a valid operation prop is required');
    }
  };

  /**
   * Calls the `submit` function to create a new catalogue or save an existing draft.
   * @param data
   */
  const saveDraft = async (data: Record<string, any>) => {
    setSaving(true);
    try {
      await submit(data);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Calls the `edit` function but passes the `isActive` flag as true.
   * @param data
   */
  const publish = async (data: Record<string, any>) => {
    // Manually trigger the 'required' form validation if trying to publish
    // and there are no animals added
    if (!data[CatalogueFormField.Animals]?.length) {
      setError(CatalogueFormField.Animals, 'required', catalogueFormValidation.animals.required);
      return;
    }

    setPublishing(true);
    try {
      await edit(data, true);
      const { query } = navigation.getCurrentValue().url;
      const href = getBackToCataloguesQuerystring(query);
      navigation.navigate(href);
    } finally {
      setPublishing(false);
    }
  };

  /**
   * Returns the component for the from step.
   * @param stepNumber The step number.
   */
  const getStepComponent = (saleType: SaleType, stepNumber: number) => {
    switch (saleType) {
      case SaleType.PrivateTreatySale:
      case SaleType.SingleVendorAuction:
      default:
        switch (stepNumber) {
          case 1:
          default:
            return (
              <CreateSaleCatalogue
                catalogue={catalogue}
                apiErrors={apiErrors}
              />
            );
          case 2:
            return <AddDetails catalogue={catalogue!} />;
          case 3:
            return <AddAnimals catalogue={catalogue!} />;
        }
      case SaleType.AllSemenCatalogue:
        switch (stepNumber) {
          case 1:
          default:
            return (
              <CreateSemenCatalogue
                catalogue={catalogue}
                apiErrors={apiErrors}
              />
            );
          case 2:
            return <AddDetails catalogue={catalogue!} />;
          case 3:
            return <AddAnimals catalogue={catalogue!} />;
        }
    }
  };

  /**
   * Gets the save button label for the form step.
   * @param stepNumber
   */
  const getSaveLabel = (stepNumber: number) => {
    switch (stepNumber) {
      case 1:
      case 2:
        return 'Save & Continue';
      case 3:
        return 'Save';
      default:
        throw Error('Invalid step number');
    }
  };

  /**
   * Gets the save button variant.
   * @param stepNumber
   */
  const getSaveVariant = (stepNumber: number): 'outlined' | 'contained' => {
    switch (stepNumber) {
      case 3:
        return 'outlined';
      default:
        return 'contained';
    }
  };

  return (
    <Hidden xsDown>
      <div className="sg-create-catalogue-form">
        <BackToCatalogue />
        <div className="header-container">
          <Container>
            <h2>{getHeader(type, catalogue?.id !== undefined || false)}</h2>
            <div className="header__step">
              <div className="text--bold">Step {step} of 3</div>
              <div>{getStepLabel(step, catalogue?.id !== undefined || false)}</div>
              <div className="container-padding">
                <span className="prime__dropdown">
                  <InputLabel>Quick database selection</InputLabel>
                  <CascadingMenu
                    id="sg-dropdown-db"
                    items={databaseTypes}
                    selected={(val) => updateDB(val)}
                    default={defaultDatabase?.value}
                  />
                </span>
              </div>
            </div>
          </Container>
        </div>
        <form>
          <Container>
            <FormContext {...methods}>
              {getStepComponent(type, step)}
            </FormContext>
          </Container>
          <FooterBar>
            {step !== 1 && (
              <Button
                variant="outlined"
                onClick={back}
              >
                Back
              </Button>
            )}
            <div className="footer__save">
              <Button
                variant={getSaveVariant(step)}
                loading={saving}
                disabled={publishing}
                onClick={handleSubmit(saveDraft)}
              >
                {getSaveLabel(step)}
              </Button>
              {step === 3 && (
                <Button
                  loading={publishing}
                  disabled={saving}
                  onClick={handleSubmit(publish)}
                >
                  Publish
                </Button>
              )}
            </div>
          </FooterBar>
        </form>
      </div>
    </Hidden>
  );
};

// (CreateSaleForm as any).whyDidYouRender = {
//   customName: 'CreateSaleForm',
// };
